import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelection } from '../../../../../../store/reducers/selection';
import { useFormContext } from 'react-hook-form';
import { AuthSmsContext } from '../../../../creator/auth.context';
import { FORM_FIELDS, PAGE } from '../../../../../../constants';
import { getSignInParams, useAutoFieldsManager, validatePhone } from '../helpers';
import { Button, Typography, Alert } from '@sravni/react-design-system';
import {
    AutoBrandSuggestField,
    AutoModelSuggestField,
    AutoYearSuggestField,
    InitialFeeField,
    PhoneField,
    SmsCodeField,
} from '../fields';
import { StepContext } from '../../../../creator/step.context';
import { useFormData } from '../../../../../../store/selectors/formStore';
import { getParamsForAuth } from '../../../../../form.common/helpers';
import { postMessage, PostMessageType } from '../../../../../../helpers/whitelable';
import styles from './styles.module.scss';
import { Col, Row } from '../../../../../landing.common';
import { SETTINGS } from '../../../../steps/params/auth';
import { useLoggedIn } from '../../../../../../store/reducers/user';
import { FocusFieldWrapper } from '../../common';
import { useIsMobile } from '@sravni/react-utils';

const { Text } = Typography;

export const AutoDesktopFields = () => {
    const { autoModelIsNeeded, autoYearIsNeeded } = useAutoFieldsManager();

    return (
        <>
            <Row gap="16" mobileGap="12">
                <Col md-4>
                    <FocusFieldWrapper name={FORM_FIELDS.INITIAL_FEE} field={<InitialFeeField />} />
                </Col>
                <Col md-4>
                    <FocusFieldWrapper name={FORM_FIELDS.AUTO_BRAND} field={<AutoBrandSuggestField />} />
                </Col>
                {autoModelIsNeeded && (
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.AUTO_MODEL} field={<AutoModelSuggestField />} />
                    </Col>
                )}
            </Row>
            {autoYearIsNeeded && (
                <Row gap="16" mobileGap="12">
                    <Col md-4>
                        <FocusFieldWrapper name={FORM_FIELDS.AUTO_YEAR} field={<AutoYearSuggestField />} />
                    </Col>
                </Row>
            )}
        </>
    );
};

export const ContactDesktopFields = () => {
    const isMobile = useIsMobile();
    const { page } = useSelection();
    const loggedIn = useLoggedIn();
    const { nextStep } = useContext(StepContext);
    const form = useFormContext();
    const formData = useFormData();
    const { checkCode, countdown, attempts, requestCode, inProcess, isSilentAuth, showSmsField, setShowingSmsField } =
        useContext(AuthSmsContext);

    const {
        watch,
        formState: { dirtyFields: _ },
    } = form;

    const [phone, code] = watch([FORM_FIELDS.PHONE, FORM_FIELDS.SMS_CODE]);

    const checkPhoneIsValid = async () => {
        const correct = phone ? await validatePhone(phone) : false;
        if (!correct) {
            setShowingSmsField(false);
        }
    };

    const onCheckCode = async (code: string) => {
        const freshUserId = await checkCode({
            ...getParamsForAuth(formData),
            code,
        });
        if (!freshUserId) {
            return;
        }

        if (page === PAGE.AVITO) {
            postMessage({
                type: PostMessageType.event,
                label: 'broker_form_num_submit',
            });
        }
        if (isSilentAuth) {
            setShowingSmsField(false);
            nextStep();
        }
    };

    useEffect(() => {
        if (code && code.length === 4 && !isMobile) {
            onCheckCode(code);
        }
    }, [code, isMobile]);

    useEffect(() => {
        if (!isMobile) {
            checkPhoneIsValid();
        }
    }, [phone, isMobile]);

    const sendSms = useCallback(async () => {
        const params = getSignInParams(phone, page);
        await requestCode(params);
    }, [requestCode, phone, page]);

    const disabledPhoneField = useMemo(
        () => loggedIn || countdown > 0 || attempts > SETTINGS.ATTEMPTS,
        [loggedIn, countdown, attempts],
    );

    return (
        <Row gap="16" mobileGap="12">
            <Col md-4>
                <FocusFieldWrapper
                    disabled={disabledPhoneField}
                    name={FORM_FIELDS.PHONE}
                    field={<PhoneField disabled={disabledPhoneField} />}
                />
            </Col>
            {!loggedIn && (
                <>
                    {showSmsField ? (
                        <Col md-4>
                            <FocusFieldWrapper name={FORM_FIELDS.SMS_CODE} field={<SmsCodeField />} />
                            {countdown > 0 && (
                                <Text className={styles['sms-field__text']} size={14}>
                                    Отправить код повторно 0:{`${countdown < 10 ? 0 : ''}${countdown}`}
                                </Text>
                            )}
                            {countdown === 0 && attempts < SETTINGS.ATTEMPTS && (
                                <Button
                                    variant="text"
                                    color="blue"
                                    className={styles['sms-field__edit-btn']}
                                    onClick={sendSms}
                                    disabled={inProcess}>
                                    Запросить новый код
                                </Button>
                            )}
                        </Col>
                    ) : (
                        <Col md-8>
                            <Alert color="orange" variant="secondary" className={styles['alert']}>
                                <Text size={14}>На этот телефон банк пришлет смс с решением</Text>
                            </Alert>
                        </Col>
                    )}
                </>
            )}
        </Row>
    );
};
